import DynamicSegmentedButton from '@/components/DynamicSegmentedButton';
import { useAnimatePlanPages } from '@/contexts/AnimatedPlanPages';
import DashboardLayout from '@/layouts/Dashboard';
import { useSelectPlanSchedulePage } from '@/stores/useSelectPlanSchedulePage';
import { pageTransition } from '@/utils/pageAnimation';
import rem from '@/utils/rem';
import { Flex, Spinner } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Suspense, lazy, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import PlanPage from './MyWeeklyPlan';

const SchedulePage = lazy(() => import('./Schedule'));

type ChildMethods = {
  onHandleSegmentedButtonSelection: (el: number) => void;
};

function WeeklyPlanPage() {
  const { t } = useTranslation('WeeklyPlan');
  const { pageNavigationEffect } = useAnimatePlanPages();
  const childMethodRef = useRef<ChildMethods | null>(null);

  const selectedPlanSchedulePageIndex = useSelectPlanSchedulePage((state) => state.selectedPlanSchedulePageIndex);
  const updateSelectedPlanSchedulePageIndex = useSelectPlanSchedulePage(
    (state) => state.updateSelectedPlanSchedulePageIndex,
  );

  useEffect(() => {
    childMethodRef.current?.onHandleSegmentedButtonSelection(selectedPlanSchedulePageIndex);
  }, [selectedPlanSchedulePageIndex]);

  const planScheduleOptions = [
    {
      value: 0,
      label: t('tabs.plan'),
    },
    {
      value: 1,
      label: t('tabs.schedule'),
    },
  ];

  return (
    <DashboardLayout
      pageTitle={t('pageTitle')}
      editActionModalTooltip={t('tooltips.editActionModalTooltip')}
      rightSectionElements={
        <DynamicSegmentedButton
          onChange={updateSelectedPlanSchedulePageIndex}
          options={planScheduleOptions}
          startIndex={selectedPlanSchedulePageIndex}
          height="full"
          innerHeight={rem(24)}
          marginTop={rem(-2)}
          layoutId="PlanSchedule"
          ref={childMethodRef}
          fullWidth
        />
      }
    >
      <Suspense
        fallback={
          <Flex justifyContent="center" marginTop={rem(80)}>
            <Spinner size="xl" speed="0.65s" thickness={rem(4)} />
          </Flex>
        }
      >
        <motion.div
          animate="in"
          exit="out"
          initial="initial"
          variants={pageNavigationEffect}
          transition={pageTransition}
        >
          {selectedPlanSchedulePageIndex === 0 && <PlanPage />}
          {selectedPlanSchedulePageIndex === 1 && <SchedulePage />}
        </motion.div>
      </Suspense>
    </DashboardLayout>
  );
}

export default WeeklyPlanPage;
